import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { io, type Socket } from "socket.io-client";
import { auth, useSession } from "./api";

export type RtEvent<T = unknown> = { id: string; type: string; companyId: string; occurredAt: string; entity: { type: string; id: string }; data: T };

let socket: Socket | null = null;
let token = "";

/**
 * Одно соединение Socket.IO на сессию сотрудника (`/platform/ws`, auth { token }). Комнаты назначает сервер по роли.
 * WS — только сигнал: после переподключения все запросы перезапрашиваются.
 */
function ensure(onReconnect: () => void) {
  const next = auth.get()?.token ?? "";
  if (next === token && (socket || !next)) return socket;
  socket?.disconnect();
  socket = null;
  token = next;
  if (!next) return null;
  const s = io({ path: "/platform/ws", transports: ["websocket", "polling"], auth: { token: next }, reconnectionDelay: 500, reconnectionDelayMax: 5000 });
  let wasOnline = false;
  s.on("ready", () => {
    if (wasOnline) onReconnect();
    wasOnline = true;
  });
  // Токен отозван или истёк: без refresh у платформы — ждём нового входа (REST сам разлогинит по 401)
  s.on("auth.error", () => s.disconnect());
  socket = s;
  return s;
}

/** Подписка на событие платформы; обработчик всегда актуальный. */
export function usePlatformEvent<T>(type: string, handler: (e: RtEvent<T>) => void) {
  const qc = useQueryClient();
  const tok = useSession()?.token;
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => {
    const s = ensure(() => void qc.invalidateQueries());
    if (!s) return;
    const fn = (e: RtEvent<T>) => ref.current(e);
    s.on(type, fn);
    return () => void s.off(type, fn);
  }, [type, tok, qc]);
}
